/**
 * File Matcher Module - Orchestrator
 * Handles file matching against search conditions
 */

"use strict";

import { debugMode } from './constants.js';
import { splitConditionsByBoost, extractPatterns } from './condition-utils.js';
import { myEachSinglePass } from './engine.js';
import { matchConditions } from './legacy.js';
import { searchStateManager } from './search-state.js';

/**
 * Search files against match conditions (equivalent to myEach from main.js)
 * Implements two-pass search with pause/resume support:
 * - Pass 1: HIGH PRIORITY (exact phrase, boost 5) - full scan
 * - Pass 2: LOW PRIORITY (individual terms, lower boost) - full scan, skipping HIGH PRIORITY results
 * @param {Array} strs - Array of file objects
 * @param {Array} matchCond - Match conditions
 * @param {boolean} full - Whether to use full path matching
 * @param {boolean} colorize - Whether to colorize matches
 * @param {boolean} test - Test mode
 * @param {number|null|undefined} resultLimit - Maximum results before pausing (null/undefined = no limit, return array)
 * @param {boolean} resume - Whether to resume from previous pause
 * @returns {Array|Object} Array if resultLimit is null/undefined, otherwise { results: Array, isComplete: boolean, canContinue: boolean }
 */
export function myEach(strs, matchCond, full = false, colorize = false, test = false, resultLimit = undefined, resume = false) {
    // Backward compatibility: if resultLimit is undefined, return array as before
    const usePauseResume = resultLimit !== undefined && resultLimit !== null;
    // For non-colorize mode, implement two-pass search with pause/resume
    if (!colorize && matchCond && matchCond.length > 0) {
        const { exactPhraseConditions, regularConditions, wasOrWrapper } = splitConditionsByBoost(matchCond);
        
        if (exactPhraseConditions.length > 0 || regularConditions.length > 0) {
            if (usePauseResume) {
                // NEW: Two-pass search with pause/resume support
                // Initialize search state
                const query = JSON.stringify(matchCond); // Use conditions as query identifier
                if (!resume) {
                    searchStateManager.startSearch(query, strs);
                }
                
                const state = searchStateManager;
                const exactPhraseResults = new Map(); // Map<fileKey, resultItem> to avoid duplicates
                
                if (debugMode === '1') {
                    console.log('[SEARCH] Conditions split:', {
                        highPriority: exactPhraseConditions.length,
                        lowPriority: regularConditions.length,
                        wasOrWrapper: wasOrWrapper
                    });
                }
                
                // Pass 1: HIGH PRIORITY - exact phrase pattern (boost 5) - FULL SCAN
                if (!state.isHighPriorityComplete && exactPhraseConditions.length > 0) {
                    if (debugMode === '1') {
                        console.log('[SEARCH] Pass 1: HIGH PRIORITY - scanning', strs.length, 'files', '(inputList length)');
                    }
                    
                    const exactMatches = myEachSinglePass(strs, exactPhraseConditions, full, colorize, test, Infinity);
                    
                    for (const item of exactMatches) {
                        const fileKey = `${item.p || ''}|${item.n || ''}`;
                        exactPhraseResults.set(fileKey, item);
                        state.highPriorityFoundKeys.add(fileKey);
                        state.highPriorityResults.push(item);
                    }
                    
                    state.isHighPriorityComplete = true;
                    
                    if (debugMode === '1') {
                        console.log('[SEARCH] Pass 1 complete:', state.highPriorityResults.length, 'HIGH PRIORITY results');
                    }
                    
                    // Check if we can pause after HIGH PRIORITY
                    if (state.canPause(resultLimit)) {
                        if (debugMode === '1') {
                            console.log('[SEARCH] Paused after Pass 1:', state.getCurrentResults().length, 'results (limit:', resultLimit, ')');
                        }
                        return {
                            results: state.getCurrentResults(),
                            isComplete: false,
                            canContinue: true
                        };
                    }
                }
                
                // Pass 2: LOW PRIORITY - individual terms (lower boost) - FULL SCAN, skipping HIGH PRIORITY results
                if (debugMode === '1') {
                    console.log('[SEARCH] Pass 2 check:', {
                        isLowPriorityComplete: state.isLowPriorityComplete,
                        regularConditionsLength: regularConditions.length,
                        wasOrWrapper: wasOrWrapper,
                        exactPhraseConditionsLength: exactPhraseConditions.length,
                        willRun: !state.isLowPriorityComplete && regularConditions.length > 0 && (wasOrWrapper || exactPhraseConditions.length === 0)
                    });
                }
                
                if (!state.isLowPriorityComplete && regularConditions.length > 0 && (wasOrWrapper || exactPhraseConditions.length === 0)) {
                    const skippedCount = state.highPriorityFoundKeys.size;
                    const filesToSearchCount = resume 
                        ? (strs.length - state.lowPriorityProcessedIndex)
                        : (strs.length - skippedCount);
                    
                    if (debugMode === '1') {
                        console.log('[SEARCH] Pass 2: LOW PRIORITY - scanning', filesToSearchCount, 'files (skipping', skippedCount, 'HIGH PRIORITY)');
                    }
                    
                    // Filter out files already found by HIGH PRIORITY
                    const filesToSearch = resume 
                        ? strs.slice(state.lowPriorityProcessedIndex)
                        : strs.filter(str => {
                            const fileKey = `${str.p || ''}|${str.n || ''}`;
                            return !state.highPriorityFoundKeys.has(fileKey);
                        });
                    
                    const regularMatches = myEachSinglePass(filesToSearch, regularConditions, full, colorize, test, Infinity);
                    
                    for (const item of regularMatches) {
                        const fileKey = `${item.p || ''}|${item.n || ''}`;
                        if (!exactPhraseResults.has(fileKey) && !state.highPriorityFoundKeys.has(fileKey)) {
                            state.lowPriorityResults.push(item);
                        }
                    }
                    
                    state.lowPriorityProcessedIndex = strs.length;
                    state.isLowPriorityComplete = true;
                    
                    if (debugMode === '1') {
                        console.log('[SEARCH] Pass 2 complete:', state.lowPriorityResults.length, 'LOW PRIORITY results');
                    }
                }
                
                // Combine and sort results
                const exactPhraseResultsArray = [...state.highPriorityResults];
                const regularResultsArray = [...state.lowPriorityResults];
                
                exactPhraseResultsArray.sort((a, b) => (b.score || 0) - (a.score || 0));
                regularResultsArray.sort((a, b) => (b.score || 0) - (a.score || 0));
                
                const finalResults = [...exactPhraseResultsArray, ...regularResultsArray];
                finalResults.forEach(item => {
                    item._twoPassSearch = true;
                    item._hasExactPhrase = state.highPriorityFoundKeys.has(`${item.p || ''}|${item.n || ''}`);
                });
                
                const isComplete = state.isComplete();
                
                if (debugMode === '1') {
                    console.log('[SEARCH] Complete:', {
                        highPriority: exactPhraseResultsArray.length,
                        lowPriority: regularResultsArray.length,
                        total: finalResults.length
                    });
                }
                
                return {
                    results: finalResults,
                    isComplete: isComplete,
                    canContinue: false
                };
            } else {
                // OLD: Two-pass search without pause/resume (backward compatibility)
                const exactPhraseResults = new Map(); // Map<fileKey, resultItem> to avoid duplicates
                const allResults = [];
                
                // Pass 1: Find files matching exact phrase conditions
                if (exactPhraseConditions.length > 0) {
                    const exactMatches = myEachSinglePass(strs, exactPhraseConditions, full, colorize, test, Infinity);
                    for (const item of exactMatches) {
                        const fileKey = `${item.p || ''}|${item.n || ''}`;
                        exactPhraseResults.set(fileKey, item);
                        allResults.push(item);
                    }
                }
                
                // Pass 2: Search regular patterns, skipping HIGH PRIORITY results
                if (regularConditions.length > 0 && (wasOrWrapper || exactPhraseConditions.length === 0)) {
                    const regularMatches = myEachSinglePass(strs, regularConditions, full, colorize, test, Infinity);
                    for (const item of regularMatches) {
                        const fileKey = `${item.p || ''}|${item.n || ''}`;
                        if (!exactPhraseResults.has(fileKey)) allResults.push(item);
                    }
                }
                
                const exactPhraseResultsArray = [];
                const regularResultsArray = [];
                for (const item of allResults) {
                    const fileKey = `${item.p || ''}|${item.n || ''}`;
                    if (exactPhraseResults.has(fileKey)) exactPhraseResultsArray.push(item);
                    else regularResultsArray.push(item);
                }
                
                exactPhraseResultsArray.sort((a, b) => (b.score || 0) - (a.score || 0));
                regularResultsArray.sort((a, b) => (b.score || 0) - (a.score || 0));
                
                const finalResults = [...exactPhraseResultsArray, ...regularResultsArray];
                finalResults.forEach(item => {
                    item._twoPassSearch = true;
                    item._hasExactPhrase = exactPhraseResults.has(`${item.p || ''}|${item.n || ''}`);
                });
                
                return finalResults; // Return array for backward compatibility
            }
        }
    }
    
    // Fallback to single-pass search
    const results = myEachSinglePass(strs, matchCond, full, colorize, test, null);
    return usePauseResume ? {
        results: results,
        isComplete: true,
        canContinue: false
    } : results; // Return array if not using pause/resume
}

/**
 * Highlight text with search conditions
 * @param {string} text - Text to highlight
 * @param {Array} matchCond - Search conditions
 * @returns {string} - Highlighted text
 */
export function highlightText(text, matchCond) {
    if (!text || typeof text !== 'string') {
        if (debugMode === '1') console.warn('[highlightText] Invalid text provided:', text);
        return text || '';
    }
    
    let highlightedText = text;
    if (window.$ && window.$.each) {
        const allPatterns = extractPatterns(matchCond);
        // Dedup identical patterns (recursion in extractPatterns can emit duplicates from nested OR/AND).
        const seen = new Set();
        for (const { pattern, modifier, flags } of allPatterns) {
            const patternFlags = (flags !== undefined && flags !== null) ? flags : (modifier || "i");
            const dedupKey = pattern + ' ' + patternFlags;
            if (seen.has(dedupKey)) continue;
            seen.add(dedupKey);
            try {
                const substrRegex = new RegExp("(" + pattern + ")", patternFlags + "g");
                highlightedText = highlightedText.replace(substrRegex, '<b>$1</b>');
            } catch (e) {
                if (debugMode === '1') console.warn('[highlightText] Invalid pattern:', pattern, e);
            }
        }
    }
    return highlightedText;
}

// Re-export core function for compatibility if needed
export { myEachSinglePass, matchConditions };

